import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import TaxService from '../../services/TaxService';
import TaxConfigurationForm from '../../components/tax/TaxConfigurationForm';
import TaxConfigurationList from '../../components/tax/TaxConfigurationList';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tax-tabpanel-${index}`}
      aria-labelledby={`tax-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function TaxConfiguration() {
  const [activeTab, setActiveTab] = useState(0);
  const [taxConfigurations, setTaxConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingConfig, setEditingConfig] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, config: null });

  useEffect(() => {
    fetchTaxConfigurations();
  }, []);

  const fetchTaxConfigurations = async () => {
    try {
      setLoading(true);
      const configs = await TaxService.getAllTaxConfigurations();
      setTaxConfigurations(configs);
      setError('');
    } catch (err) {
      setError('Failed to fetch tax configurations: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      setShowForm(false);
      setEditingConfig(null);
    }
  };

  const handleAddNew = () => {
    setEditingConfig(null);
    setShowForm(true);
    setActiveTab(1);
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setShowForm(true);
    setActiveTab(1);
  };

  const handleDelete = (config) => {
    setDeleteDialog({ open: true, config });
  };

  const confirmDelete = async () => {
    try {
      await TaxService.deleteTaxConfiguration(deleteDialog.config.taxConfigId);
      setSuccess('Tax configuration deleted successfully');
      fetchTaxConfigurations();
      setDeleteDialog({ open: false, config: null });
    } catch (err) {
      setError('Failed to delete tax configuration: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSetDefault = async (config) => {
    try {
      await TaxService.setDefaultTaxConfiguration(config.taxConfigId);
      setSuccess('Default tax configuration updated successfully');
      fetchTaxConfigurations();
    } catch (err) {
      setError('Failed to set default tax configuration: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleFormSuccess = (message) => {
    setSuccess(message);
    setShowForm(false);
    setEditingConfig(null);
    setActiveTab(0);
    fetchTaxConfigurations();
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingConfig(null);
    setActiveTab(0);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h4" component="h1">
              Tax Configuration
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              sx={{ ml: 2 }}
            >
              Add Tax Configuration
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
              <Tab label="Tax Configurations" />
              <Tab label={editingConfig ? 'Edit Configuration' : 'Add Configuration'} disabled={!showForm} />
            </Tabs>
          </Box>

          <TabPanel value={activeTab} index={0}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TaxConfigurationList
                configurations={taxConfigurations}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onSetDefault={handleSetDefault}
              />
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            {showForm && (
              <TaxConfigurationForm
                configuration={editingConfig}
                onSuccess={handleFormSuccess}
                onCancel={handleFormCancel}
              />
            )}
          </TabPanel>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, config: null })}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the tax configuration "{deleteDialog.config?.taxName}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, config: null })}>
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TaxConfiguration;